import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Alert, Button, Stack, CircularProgress } from '@mui/material'
import { TitledPaper } from '@dai/components'
import { Link as RouterLink } from 'react-router-dom'

export interface HandleVerifyEmailProps {
  oobCode: string
  continueUrl: string
  verifyEmail: (oobCode: string) => Promise<void>
}

const HandleVerifyEmail: React.FC<HandleVerifyEmailProps> = ({
  oobCode,
  continueUrl,
  verifyEmail,
}) => {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const verificationAttempted = useRef<boolean>(false)

  const ERROR_MESSAGES: Record<string, string> = useMemo(
    () => ({
      'auth/expired-action-code':
        'Your request to verify your email has expired or the link has already been used.',
      'auth/invalid-action-code':
        'Your request to verify your email has expired or the link has already been used.',
      'auth/user-not-found':
        'The user account associated with this email could not be found.',
    }),
    [],
  )

  useEffect(() => {
    const verifyEmailAddress = async (code: string) => {
      if (verificationAttempted.current) {
        return
      }

      verificationAttempted.current = true

      try {
        setLoading(true)
        await verifyEmail(code)
      } catch (error: any) {
        setError(
          ERROR_MESSAGES[error.code] || error.message || 'Something went wrong',
        )
      } finally {
        setLoading(false)
      }
    }
    verifyEmailAddress(oobCode)
  }, [oobCode, verifyEmail, ERROR_MESSAGES])

  if (loading) {
    return (
      <TitledPaper
        title="Verify Email"
        subtitle="Verifying your email address..."
      >
        <CircularProgress />
      </TitledPaper>
    )
  }

  return (
    <TitledPaper
      title="Email Verification"
      subtitle={
        error ? 'Try verifying your email again' : 'Email verification complete'
      }
    >
      <Stack spacing={4} width="100%">
        {error ? (
          <Alert severity="warning">{error}</Alert>
        ) : (
          <Alert severity="success">
            Your email has been successfully verified.
          </Alert>
        )}
        <Button
          fullWidth
          color="primary"
          variant="contained"
          component={RouterLink}
          to={continueUrl}
        >
          Continue
        </Button>
      </Stack>
    </TitledPaper>
  )
}

export default HandleVerifyEmail
